import { useCallback, useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Checkbox,
  Dialog,
  Flex,
  ImagePlusIcon,
  Input,
  Label,
  Text,
  VisuallyHidden,
  toast,
  useLocalStorage,
} from '@buffer-mono/popcorn'
import {
  FileUploadConfig,
  UploadSource,
  UploadType,
  useUploader,
} from '@buffer-mono/uploader'

import { UploadButton } from '~publish/components/UploadButton'
import * as UploadDropzone from '~publish/components/UploadDropzone'
import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import type { InsightsExportSection } from '../../graphqlEnums'

import styles from './ExportConfigDialog.module.css'

/** The two Insights surfaces that can trigger a PDF export. */
export type ExportConfigVariant = 'single' | 'allChannels'

/** The customization the user picked in the dialog, passed to the export. */
export type InsightsExportConfig = {
  /** Sections to omit (deny-list). Empty means the full report. */
  excludedSections: InsightsExportSection[]
  /** Persistent asset URL of an uploaded logo, or undefined for none. */
  customLogoUrl?: string
  /** Custom report name for the PDF header, or undefined to use the org name. */
  customReportName?: string
}

interface ExportConfigDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which surface opened the dialog — determines the toggleable sections. */
  variant: ExportConfigVariant
  /** Called with the chosen configuration when the user confirms. */
  onConfirm: (config: InsightsExportConfig) => void
}

// The dialog is organised into checkbox groups per surface. Channel-level
// values (`summary`…`visibility`) govern the single-channel report and, in the
// all-channels report, every per-channel breakdown. Aggregate values are the
// all-channels report's own top-level containers. The cover identity always
// renders and isn't represented here.
type SectionOption = { key: InsightsExportSection; label: string }

type SectionGroupDef = {
  label: string
  /** When set, the group is gated by this master toggle (sub-options disable when off). */
  master?: InsightsExportSection
  masterLabel?: string
  /** Column count for this group's checkboxes. Defaults to 2. */
  columns?: 1 | 2
  options: SectionOption[]
}

// Channel-level charts, in report order (4 simple + 3 narrative). These render
// under the "Charts" group heading, so the labels omit the redundant "chart".
const CHANNEL_CHART_OPTIONS: SectionOption[] = [
  { key: 'followersChart', label: 'Followers' },
  { key: 'reachChart', label: 'Reach' },
  { key: 'viewsChart', label: 'Profile Views' },
  { key: 'postsChart', label: 'Posts' },
  { key: 'contentImpact', label: 'Content Impact' },
  { key: 'audienceGrowth', label: 'Audience Growth' },
  { key: 'visibility', label: 'Visibility' },
]

const GROUPS_BY_VARIANT: Record<ExportConfigVariant, SectionGroupDef[]> = {
  single: [
    {
      label: 'Sections',
      columns: 1,
      options: [
        { key: 'summary', label: 'Summary' },
        { key: 'performancePerPost', label: 'Performance per post' },
      ],
    },
    { label: 'Charts', columns: 2, options: CHANNEL_CHART_OPTIONS },
  ],
  allChannels: [
    {
      label: 'Report',
      options: [
        { key: 'aggregateSummary', label: 'Summary' },
        { key: 'aggregateTopPosts', label: 'Top 5 posts' },
        { key: 'channelSummaryTable', label: 'Channel summary table' },
        { key: 'aggregateFollowersChart', label: 'Followers chart' },
        { key: 'aggregatePostsChart', label: 'Posts chart' },
      ],
    },
    {
      label: 'Per-channel breakdown',
      master: 'perChannelBreakdown',
      masterLabel: 'Include a section for each channel',
      options: [
        { key: 'summary', label: 'Summary' },
        { key: 'performancePerPost', label: 'Performance per post' },
        ...CHANNEL_CHART_OPTIONS,
      ],
    },
  ],
}

// All toggleable keys for a variant (group masters + their options), used to
// seed the default-on set and compute the excluded deny-list on confirm.
function applicableKeysFor(
  variant: ExportConfigVariant,
): InsightsExportSection[] {
  return GROUPS_BY_VARIANT[variant].flatMap((group) => [
    ...(group.master ? [group.master] : []),
    ...group.options.map((option) => option.key),
  ])
}

type StoredConfig = {
  excludedSections: InsightsExportSection[]
  customLogoUrl: string | null
  customReportName: string | null
}

// Persisted per variant (the applicable sections differ) so reopening the
// dialog restores the user's last choices — matching the column-picker's
// localStorage persistence.
const storageKey = (variant: ExportConfigVariant): string =>
  `insights:export-config:${variant}`

const DEFAULT_CONFIG: StoredConfig = {
  excludedSections: [],
  customLogoUrl: null,
  customReportName: null,
}

const logoRestrictions = {
  maxNumberOfFiles: 1,
  allowedFileTypes: ['.jpg', '.jpeg', '.png', '.webp'],
  uploadConfig: FileUploadConfig.IMAGE,
}

const MAX_LOGO_SIZE = 5 * 1024 * 1024 // 5 MB

/**
 * Pre-export dialog (PDF only) letting the user toggle report sections on/off
 * (all on by default) and upload a custom logo for the report cover. On confirm
 * it emits the deny-list of excluded sections plus the uploaded logo URL, and
 * persists both so the next export starts from the same choices.
 */
export function ExportConfigDialog({
  open,
  onOpenChange,
  variant,
  onConfirm,
}: ExportConfigDialogProps): JSX.Element {
  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  const groups = GROUPS_BY_VARIANT[variant]
  const applicableKeys = useMemo(() => applicableKeysFor(variant), [variant])

  const [stored, setStored] = useLocalStorage<StoredConfig>(
    storageKey(variant),
    DEFAULT_CONFIG,
  )

  const [includedSections, setIncludedSections] = useState<
    Set<InsightsExportSection>
  >(new Set())
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [reportName, setReportName] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  // Re-sync the working copy from the persisted config whenever the dialog
  // opens, so a Cancel discards any in-dialog edits. Only keyed on `open` on
  // purpose — mid-session localStorage writes shouldn't clobber live edits.
  useEffect(() => {
    if (!open) return
    const excluded = new Set(stored.excludedSections)
    setIncludedSections(
      new Set(applicableKeys.filter((key) => !excluded.has(key))),
    )
    setLogoUrl(stored.customLogoUrl)
    setReportName(stored.customReportName ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const uploader = useUploader({
    id: UploadType.clientLogo,
    userId: accountId ?? '',
    organizationId: organizationId ?? '',
    fileRestrictions: logoRestrictions,
  })

  const handleLogoFiles = useCallback(
    async (files: File[], source: UploadSource): Promise<void> => {
      const file = files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        toast.error('Logo must be an image (JPG, PNG, or WebP).')
        return
      }
      if (file.size > MAX_LOGO_SIZE) {
        toast.error('Logo must be under 5 MB.')
        return
      }

      setIsUploading(true)
      try {
        const result = await uploader.upload([file], { source })
        const uploadedUrl = result.successful[0]?.uploadURL
        if (uploadedUrl) {
          setLogoUrl(uploadedUrl)
        } else {
          toast.error('Logo upload failed. Please try again.')
        }
      } catch {
        toast.error('Logo upload failed. Please try again.')
      } finally {
        setIsUploading(false)
        uploader.uppyInstance.cancelAll()
      }
    },
    [uploader],
  )

  const toggleSection = (key: InsightsExportSection): void => {
    setIncludedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // Export requires at least one rendered section, and any enabled per-channel
  // breakdown to have a chart selected (an empty one prints blank channel pages).
  const hasSelection = groups.some(
    (group) =>
      (!group.master || includedSections.has(group.master)) &&
      group.options.some((option) => includedSections.has(option.key)),
  )
  const perChannelEmpty = groups.some(
    (group) =>
      group.master != null &&
      includedSections.has(group.master) &&
      !group.options.some((option) => includedSections.has(option.key)),
  )
  const canExport = hasSelection && !perChannelEmpty

  const handleConfirm = (): void => {
    if (!canExport) return
    const excludedSections = applicableKeys.filter(
      (key) => !includedSections.has(key),
    )
    const trimmedName = reportName.trim()
    setStored({
      excludedSections,
      customLogoUrl: logoUrl,
      customReportName: trimmedName || null,
    })
    onConfirm({
      excludedSections,
      customLogoUrl: logoUrl ?? undefined,
      customReportName: trimmedName || undefined,
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content>
        <Dialog.Header>
          <Dialog.Title>Export as PDF</Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description>
              Name your report, add an optional logo, and choose which sections
              to include. All sections are included by default.
            </Dialog.Description>
          </VisuallyHidden>
        </Dialog.Header>
        <Dialog.Body>
          <Flex direction="column" gap="lg">
            <Flex direction="column" gap="xs" fullWidth>
              <Label htmlFor="export-report-name">Report name</Label>
              <Input
                className={styles.reportName}
                id="export-report-name"
                value={reportName}
                onChange={(event) => setReportName(event.target.value)}
                placeholder="Defaults to your organization name"
              />
            </Flex>

            <UploadDropzone.Root
              className={styles.logoDropzone}
              disabled={isUploading}
              onDrop={(files) =>
                handleLogoFiles(files, UploadSource.dragAndDrop())
              }
            >
              <Flex direction="column" gap="xs">
                <Label>
                  Logo{' '}
                  <Text as="span" size="sm" color="subtle">
                    · Optional
                  </Text>
                </Label>
                <Text size="sm" color="subtle">
                  PNG, JPG, or WebP, up to 5 MB.
                </Text>
                <Flex direction="row" align="center" gap="sm">
                  <UploadButton
                    busy={isUploading}
                    previewSrc={logoUrl ?? undefined}
                    onFileChange={(files) =>
                      handleLogoFiles(files, UploadSource.filePicker())
                    }
                  />
                  {logoUrl && !isUploading && (
                    <Button variant="tertiary" onClick={() => setLogoUrl(null)}>
                      Remove logo
                    </Button>
                  )}
                </Flex>
              </Flex>
              <UploadDropzone.Overlay>
                <UploadDropzone.Content>
                  <UploadDropzone.Icon>
                    <ImagePlusIcon size="large" />
                  </UploadDropzone.Icon>
                  <UploadDropzone.Description>
                    Drop logo here
                  </UploadDropzone.Description>
                </UploadDropzone.Content>
              </UploadDropzone.Overlay>
            </UploadDropzone.Root>

            {groups.map((group) => {
              const master = group.master
              const masterOn = master ? includedSections.has(master) : true
              const masterEmpty =
                master != null &&
                masterOn &&
                !group.options.some((option) =>
                  includedSections.has(option.key),
                )
              const groupClassName =
                group.label === 'Report'
                  ? styles.reportSpacing
                  : master === 'perChannelBreakdown'
                    ? styles.perChannelGroup
                    : undefined
              return (
                <Checkbox.Group
                  key={group.label}
                  label={group.label}
                  className={groupClassName}
                >
                  <Flex direction="column" gap="xs">
                    {master && (
                      <Flex direction="row" align="center" gap="sm">
                        <Checkbox
                          checked={masterOn}
                          onChange={() => toggleSection(master)}
                        >
                          {group.masterLabel ?? group.label}
                        </Checkbox>
                        {masterEmpty && (
                          <Text size="sm" color="critical" role="alert">
                            One chart must be selected.
                          </Text>
                        )}
                      </Flex>
                    )}
                    {master ? (
                      // Collapse the sub-toggles when the breakdown is off; they
                      // stay disabled while collapsed so they're not tabbable.
                      <div
                        className={styles.collapsible}
                        data-open={masterOn}
                        aria-hidden={!masterOn}
                      >
                        <div className={styles.collapsibleInner}>
                          <div className={styles.card}>
                            <div
                              className={styles.optionsGrid}
                              data-columns={group.columns ?? 2}
                            >
                              {group.options.map((option) => (
                                <Checkbox
                                  key={option.key}
                                  checked={includedSections.has(option.key)}
                                  disabled={!masterOn}
                                  onChange={() => toggleSection(option.key)}
                                >
                                  {option.label}
                                </Checkbox>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={styles.optionsGrid}
                        data-columns={group.columns ?? 2}
                      >
                        {group.options.map((option) => (
                          <Checkbox
                            key={option.key}
                            checked={includedSections.has(option.key)}
                            onChange={() => toggleSection(option.key)}
                          >
                            {option.label}
                          </Checkbox>
                        ))}
                      </div>
                    )}
                  </Flex>
                </Checkbox.Group>
              )
            })}
          </Flex>
        </Dialog.Body>
        <Dialog.Footer>
          {!hasSelection && !perChannelEmpty && (
            <Text
              size="sm"
              color="critical"
              role="alert"
              className={styles.footerHint}
            >
              Select at least one section or chart to include.
            </Text>
          )}
          <Dialog.Close>
            <Button variant="tertiary">Cancel</Button>
          </Dialog.Close>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isUploading || !canExport}
          >
            Export as PDF
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
